'use client';

import React, { useEffect, useRef, useState } from 'react';

type TenantHeroVideoProps = {
  src: string;
  poster: string;
};

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';
const H264_MP4 = 'video/mp4; codecs="avc1.42E01E"';

const browserCanPlayHeroVideo = (): boolean => {
  const probe = document.createElement('video');
  return typeof probe.canPlayType === 'function' && probe.canPlayType(H264_MP4) !== '';
};

/**
 * Decorative hero background video. The hero section always paints the poster
 * image itself, so this renders nothing on the server and on the first client
 * render (no hydration mismatch, no blank hero), and only adds the video once
 * the browser can play it and the visitor has not asked for reduced motion.
 * The video fades in only after it actually plays; any failure leaves the poster.
 */
const TenantHeroVideo: React.FC<TenantHeroVideoProps> = ({ src, poster }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [allowed, setAllowed] = useState(false);
  const [failed, setFailed] = useState(false);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const canPlay = browserCanPlayHeroVideo();
    const motion = typeof window.matchMedia === 'function' ? window.matchMedia(REDUCED_MOTION) : undefined;
    const update = () => setAllowed(canPlay && !motion?.matches);
    update();
    motion?.addEventListener?.('change', update);
    return () => motion?.removeEventListener?.('change', update);
  }, []);

  useEffect(() => {
    setFailed(false);
    setPlaying(false);
  }, [src]);

  useEffect(() => {
    const video = videoRef.current;
    if (!allowed || failed || !video) return undefined;
    // React does not always reflect `muted` as an attribute; set it on the
    // element so autoplay is permitted and audio can never play.
    video.muted = true;
    video.volume = 0;
    const play = () => {
      const attempt = video.play();
      // Autoplay can be refused (for example in low-power mode); the poster stays.
      if (attempt && typeof attempt.catch === 'function') attempt.catch(() => setPlaying(false));
    };
    if (typeof IntersectionObserver === 'undefined') {
      play();
      return () => video.pause();
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting) play();
      else video.pause();
    }, { threshold: 0.05 });
    observer.observe(video);
    return () => {
      observer.disconnect();
      video.pause();
    };
  }, [allowed, failed, src]);

  if (!allowed || failed) return null;
  return (
    <div className={'hero-media' + (playing ? ' is-playing' : '')} aria-hidden="true" data-testid="hero-video">
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        aria-hidden="true"
        tabIndex={-1}
        disablePictureInPicture
        disableRemotePlayback
        onPlaying={() => setPlaying(true)}
        onError={() => setFailed(true)}
      />
    </div>
  );
};

export default TenantHeroVideo;
